import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getPositionRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface PositionRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function PositionRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: PositionRowProps) {
  const { clientPosition, inWater, notes, hasAnswers, hasNotes } = useMemo(
    () => getPositionRowValues(observation),
    [observation],
  );

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Position:</TextWrapper>
            <List fz="sm" withPadding>
              {clientPosition && (
                <List.Item>
                  <strong>Client's Position:</strong> {clientPosition}
                </List.Item>
              )}
              {inWater && (
                <List.Item>
                  <strong>Client in Water:</strong> {inWater}
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Position Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={PositionRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
